import React, { useState, useEffect } from "react";
import { v4 as uuid } from "uuid";
import sortBy from "lodash/sortBy";
import * as GlobalStyled from "../styled/form-fields";
import * as Styled from "./misc/styled";
import Preview, { JsonPrint } from "../Preview";
import Topic from "./QA";
import ParagraphField from "./ParagraphField";
import QAForm from "./QAField";

const DEFAULT_VALUE = Object.freeze({
  title: "",
  header: "",
  paragraphs: [],
  btnText: "",
  qas: [],
});

const FormType = Object.freeze({
  ADD: "ADD",
  EDIT: "EDIT",
});

const TopicField = (props) => {
  const {
    input: { value, onChange },
  } = props;

  const [paragraphForm, setParagraphForm] = useState({
    value: "",
    order: "",
    formType: FormType.ADD,
  });

  const [qaForm, setQAForm] = useState({
    question: "",
    answer: "",
    order: "",
    formType: FormType.ADD,
  });

  useEffect(() => {
    if (typeof value === "string") {
      onChange({ ...DEFAULT_VALUE });
    }
  }, []);

  if (typeof value === "string") {
    return null;
  }

  const handleOnChangeParagraphForm = ({ target }) => {
    const { name, type } = target;
    const val = type === "checkbox" ? target.checked : target.value;
    setParagraphForm({
      ...paragraphForm,
      [name]: val,
    });
  };

  const handleOnKeyDownParagraphForm = (e) => {
    if (e.key === "Enter") {
      const { formType, ...values } = paragraphForm;
      if (Object.values(values).every((o) => o && o.length > 0)) {
        handleOnAddParagraph(e);
      }
    }
  };

  const handleOnAddParagraph = (e) => {
    e.preventDefault();

    const { formType, ...values } = paragraphForm;

    if (!Object.values(values).every((o) => o && o.length > 0)) {
      return;
    }

    if (formType === FormType.ADD) {
      onChange({
        ...value,
        paragraphs: [
          ...value.paragraphs,
          {
            id: uuid(),
            value: values.value,
            order: !isNaN(values.order) ? parseInt(values.order, 10) : 0,
          },
        ],
      });
    } else {
      const filteredParagraphs = value.paragraphs.filter(
        (o) => o.id !== values.id
      );
      onChange({
        ...value,
        paragraphs: [
          ...filteredParagraphs,
          {
            ...values,
          },
        ],
      });
    }

    setParagraphForm({
      value: "",
      order: "",
      formType: FormType.ADD,
    });
  };

  const handleOnCancelEditParagraph = () => {
    setParagraphForm({
      value: "",
      order: "",
      formType: FormType.ADD,
    });
  };

  const handleOnChangeQAForm = ({ target }) => {
    const { name, type } = target;
    const val = type === "checkbox" ? target.checked : target.value;
    setQAForm({
      ...qaForm,
      [name]: val,
    });
  };

  const handleOnKeyDownQAForm = (e) => {
    if (e.key === "Enter") {
      const { formType, ...values } = qaForm;
      if (Object.values(values).every((o) => o && o.length > 0)) {
        handleOnAddQA(e);
      }
    }
  };

  const handleOnAddQA = (e) => {
    e.preventDefault();

    const { formType, ...values } = qaForm;

    if (!Object.values(values).every((o) => o && o.length > 0)) {
      return;
    }

    if (formType === FormType.ADD) {
      onChange({
        ...value,
        qas: [
          ...value.qas,
          {
            id: uuid(),
            question: values.question,
            answer: values.answer,
            order: !isNaN(values.order) ? parseInt(values.order, 10) : 0,
          },
        ],
      });
    } else {
      const filteredQAs = value.qas.filter((o) => o.id !== values.id);
      onChange({
        ...value,
        qas: [
          ...filteredQAs,
          {
            ...values,
          },
        ],
      });
    }

    setQAForm({
      question: "",
      answer: "",
      order: "",
      formType: FormType.ADD,
    });
  };

  const handleOnCancelQAEdit = () => {
    setQAForm({
      question: "",
      answer: "",
      order: "",
      formType: FormType.ADD,
    });
  };

  const handleOnChange = ({ target }) => {
    const { name, type } = target;
    const val = type === "checkbox" ? target.checked : target.value;
    onChange({
      ...value,
      [name]: val,
    });
  };

  const handleOnDelete = (id, name) => {
    const filtered = value[name].filter((o) => o.id !== id);
    onChange({
      ...value,
      [name]: filtered,
    });
    setParagraphForm({
      value: "",
      order: "",
      formType: FormType.ADD,
    });
    setQAForm({
      question: "",
      answer: "",
      order: "",
      formType: FormType.ADD,
    });
  };

  const { title, header, paragraphs, btnText, qas = [] } = value;

  return (
    <section>
      <Preview>
        <Topic {...value} />
      </Preview>
      <JsonPrint disable data={value} />
      <GlobalStyled.FormField>
        <GlobalStyled.Label>Title</GlobalStyled.Label>
        <GlobalStyled.Input
          name="title"
          value={title}
          onChange={handleOnChange}
        />
      </GlobalStyled.FormField>
      <GlobalStyled.FormField>
        <GlobalStyled.Label>Header</GlobalStyled.Label>
        <GlobalStyled.Input
          name="header"
          value={header}
          onChange={handleOnChange}
        />
      </GlobalStyled.FormField>
      <GlobalStyled.FormField>
        <GlobalStyled.Label>Paragraphs</GlobalStyled.Label>
        <GlobalStyled.FormField horizontal>
          <GlobalStyled.Input
            name="value"
            style={{ flex: 8 }}
            placeholder="add new paragraph..."
            value={paragraphForm.value}
            onChange={handleOnChangeParagraphForm}
            onKeyDown={handleOnKeyDownParagraphForm}
          />
          <GlobalStyled.Input
            name="order"
            style={{ flex: 4, marginLeft: "16px" }}
            placeholder="order (invalid defaults to 0)"
            value={paragraphForm.order}
            onChange={handleOnChangeParagraphForm}
            onKeyDown={handleOnKeyDownParagraphForm}
          />
          <div style={{ marginLeft: "16px" }}>
            <a onClick={handleOnAddParagraph} role="button">
              {paragraphForm.formType === FormType.ADD ? "Add" : "Save"}
            </a>
            {paragraphForm.formType === FormType.EDIT && (
              <a
                onClick={handleOnCancelEditParagraph}
                role="button"
                style={{ marginLeft: "16px" }}
              >
                Cancel
              </a>
            )}
          </div>
        </GlobalStyled.FormField>
        {sortBy(paragraphs, ["order", "value"]).map((o) => (
          <ParagraphField
            key={o.id}
            paragraph={o}
            onEdit={() => {
              setParagraphForm({
                ...o,
                formType: FormType.EDIT,
              });
            }}
            onDelete={handleOnDelete}
          />
        ))}
      </GlobalStyled.FormField>
      <Styled.ParagraphList></Styled.ParagraphList>
      <GlobalStyled.FormField>
        <GlobalStyled.Label>Button text</GlobalStyled.Label>
        <GlobalStyled.Input
          name="btnText"
          value={btnText}
          onChange={handleOnChange}
        />
      </GlobalStyled.FormField>
      <GlobalStyled.FormField>
        <GlobalStyled.Label>Questions & Answers</GlobalStyled.Label>
        <GlobalStyled.FormField horizontal>
          <GlobalStyled.Input
            name="question"
            animateExpand
            style={{ flex: 5 }}
            placeholder="add new question"
            value={qaForm.question}
            onChange={handleOnChangeQAForm}
            onKeyDown={handleOnKeyDownQAForm}
          />
          <GlobalStyled.Input
            name="answer"
            animateExpand
            style={{ flex: 5, marginLeft: "16px" }}
            placeholder="add new answer"
            value={qaForm.answer}
            onChange={handleOnChangeQAForm}
            onKeyDown={handleOnKeyDownQAForm}
          />
          <GlobalStyled.Input
            name="order"
            style={{ flex: 2, marginLeft: "16px" }}
            placeholder="order (invalid defaults to 0)"
            value={qaForm.order}
            onChange={handleOnChangeQAForm}
            onKeyDown={handleOnKeyDownQAForm}
          />
          <div style={{ marginLeft: "16px" }}>
            <a onClick={handleOnAddQA} role="button">
              {qaForm.formType === FormType.ADD ? "Add" : "Save"}
            </a>
            {qaForm.formType === FormType.EDIT && (
              <a
                onClick={handleOnCancelQAEdit}
                role="button"
                style={{ marginLeft: "16px" }}
              >
                Cancel
              </a>
            )}
          </div>
        </GlobalStyled.FormField>
        {sortBy(qas, ["order", "value"]).map((o) => (
          <QAForm
            key={o.id}
            qa={o}
            onEdit={() => {
              setQAForm({
                ...o,
                formType: FormType.EDIT,
              });
            }}
            onDelete={handleOnDelete}
          />
        ))}
      </GlobalStyled.FormField>
    </section>
  );
};

export default TopicField;



// WEBPACK FOOTER //
// ./src/core/admin/components/QA/Field.js